/**
 * Shared, non-styling Icon logic used by BOTH render paths (the styled-components
 * Container path and the Tailwind <span> path). Factoring this out keeps the two
 * paths from diverging: name/variant resolution, viewBox lookup, aria handling,
 * and the <svg> markup live here once.
 *
 * Not part of the public export surface (not re-exported from index.ts).
 */
import * as React from "react";
import { includes } from "@sproutsocial/seeds-react-utilities";
import {
  AiViewBoxes,
  ExternalViewBoxes,
  GeneralViewBoxes,
  SproutViewBoxes,
  ExternalIconNames,
  type EnumIconSvgNames,
  type EnumIconNamesWithoutVariants,
} from "@sproutsocial/seeds-icons";
import type { TypeIconName, TypeIconSize } from "./IconTypes";

export const AllViewboxes = {
  ...AiViewBoxes,
  ...ExternalViewBoxes,
  ...GeneralViewBoxes,
  ...SproutViewBoxes,
};

/**
 * Resolve a bare icon name to its concrete svg-sprite name by applying the
 * default variant (`solid` for `mini`, `outline` otherwise) unless the name is
 * already variant-suffixed or is an external icon. Matches the original
 * Icon.tsx resolution exactly.
 */
export function resolveIconName(
  name: TypeIconName,
  size: TypeIconSize
): EnumIconSvgNames {
  const defaultVariant = size === "mini" ? "solid" : "outline";
  return !name?.endsWith("-outline") &&
    !name?.endsWith("-solid") &&
    !includes(ExternalIconNames, name)
    ? // then add default variant
      (`${
        name as EnumIconNamesWithoutVariants
      }-${defaultVariant}` as EnumIconSvgNames)
    : // else use name as is
      (name as EnumIconSvgNames);
}

/**
 * Compute the aria attributes for the container and the svg. When an aria-label
 * is present (via the `ariaLabel` prop or a spread `aria-label`), the svg gets
 * role="img" + aria-label and the container is left visible; otherwise the
 * container is marked aria-hidden. Matches the original Icon.tsx behavior.
 */
export function getAriaProps(ariaLabel?: string, ariaLabelFromRest?: string) {
  const hasAriaLabel = !!ariaLabel || !!ariaLabelFromRest;
  const containerAriaProps = hasAriaLabel
    ? {}
    : { "aria-hidden": "true" as const };
  const svgAriaProps = hasAriaLabel
    ? { role: "img" as const, "aria-label": ariaLabel ?? ariaLabelFromRest }
    : {};
  return { containerAriaProps, svgAriaProps };
}

/**
 * The icon <svg> markup, identical for both render paths. `svgAriaProps` is
 * spread last so aria attributes win over any `svgProps` (matching the original
 * Icon.tsx spread order).
 */
export const IconSvg = ({
  iconName,
  iconViewBox,
  svgProps,
  svgAriaProps,
}: {
  iconName: string;
  iconViewBox?: string;
  svgProps?: React.ComponentPropsWithoutRef<"svg">;
  svgAriaProps: React.ComponentPropsWithoutRef<"svg">;
}) => (
  <svg
    className="Icon-svg"
    viewBox={iconViewBox}
    focusable={false}
    data-qa-icon-svg={`${iconName}-svg`}
    {...svgProps}
    {...svgAriaProps}
  >
    <use
      xmlnsXlink="http://www.w3.org/1999/xlink"
      xlinkHref={`#seeds-svgs_${iconName}`}
    />
  </svg>
);
